/**
 * Capitalizes the first letter of a given string.
 *
 * @param {string} word - The input string to capitalize.
 * @returns {string} The input string with the first letter capitalized.
 *
 * @example
 * ```
 * // Returns: "Hello"
 * capitalize("hello");
 * ```
 */
export function capitalize(word: string): string {
	return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Determines the appropriate text color (black or white) based on the brightness of a given color.
 *
 * The brightness is calculated using the formula:
 * `(Red * 299 + Green * 587 + Blue * 114) / 1000`
 *
 * If the brightness is greater than 128, the function returns "black"; otherwise, it returns "white".
 *
 * @param {string} color - The hexadecimal representation of the color (e.g., "#RRGGBB").
 * @returns {string} The recommended text color ("black" or "white").
 *
 * @example
 * ```
 * //Returns: "black"
 * getTextColor("#808080");
 * ```
 */
export function getTextColor(color: string): string {
	/**
	 * Calculates the brightness of a color given its hexadecimal representation.
	 *
	 * @param {string} color - The hexadecimal representation of the color (e.g., "#RRGGBB").
	 * @returns {number} The brightness value, ranging from 0 to 255.
	 */
	function getBrightness(color: string): number {
		const hexColor = color.replace(/^#/, '');
		const r = parseInt(hexColor.slice(0, 2), 16);
		const g = parseInt(hexColor.slice(2, 4), 16);
		const b = parseInt(hexColor.slice(4, 6), 16);
		return (r * 299 + g * 587 + b * 114) / 1000;
	}

	// Determine text color based on brightness
	return getBrightness(color) > 128 ? 'black' : 'white';
}

/**
 * Removes HTML tags from a given string.
 *
 * @param input - The input string containing HTML.
 * @returns A new string with HTML tags removed.
 *
 * @example
 * ```
 * // Returns: 'This is HTML text.'
 * const stringWithHtml = '<p>This is <strong>HTML</strong> text.</p>';
 * const stringWithoutHtml = removeHtmlTags(stringWithHtml);
 * console.log(stringWithoutHtml);
 * ```
 */
export function removeHtmlTags(input: string | React.ReactNode): string {
	let conversion = '';
	if (typeof input !== 'string') {
		conversion = input?.toString() ?? '';
	} else {
		conversion = input;
	}
	const doc = new DOMParser().parseFromString(conversion, 'text/html');
	return doc.body.textContent || '';
}

/**
 * Return words in string in Title case.
 *
 * @param {string} word - The input string to capitalize.
 * @returns {string} The input string with the first letter capitalized.
 *
 * @example
 * ```
 * // Returns: "John Smith"
 * toTitleCase("john smith");
 * ```
 */

export function toTitleCase(str: string) {
	return str.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
}
